import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import RichTextEditor from 'Components/UI/RichTextEditor';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import Button from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Button';
import RichTextDisplay from 'Components/UI/Overrides/RichTextDisplay';
import useMobileLayout from 'Hooks/useMobileLayout';
import TemplateOptionsMenu from 'Components/UI/TemplateOptionsMenu';
import { TEMPLATE_TYPES } from 'Components/UI/TemplateOptionsMenu/utils';
import { useTemplatesStateContext } from 'Context/TemplateContext';

const styles = theme => ({
  root: {
    padding: theme.spacing(4),
    minWidth: '100%',
    backgroundColor: theme.palette.common.white,
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '1.2em',
  },
  actions: {
    paddingTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: '-50px', // <--- This positions the "update" button in-line with the CKEditor action-items
    alignItems: 'center',
  },
  actionsMobile: {
    marginTop: 0,
  },
  richTextDisplay: {
    marginTop: theme.spacing(2),
  },
  richTextEditor: {
    width: '100%',
    display: 'flex',
    border: 'none',
    '& .cke_wysiwyg_frame': {
      borderBottom: `1px solid ${theme.palette.facet.accentGray}`,
    },
    '& .cke': {
      width: '100%',
    },
    '& .cke_top': {
      display: 'flex',
      overflow: 'unset',
    },
    '& .cke_inner': {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    '& .cke_contents': {
      maxWidth: '100%',
      fontFamily: theme.typography.fontFamily,
      padding: theme.spacing(2, 0),
      display: 'flex',
    },
    '& .cke_ltr': {
      border: 0,
    },
    '& .cke_bottom': {
      padding: 0,
      background: 'transparent',
      borderTop: 'none',
    },
  },
});

const OutOfOfficeMessageEditor = ({
  className,
  classes,
  setOutOfOfficeMessage,
  outOfOfficeMessage,
  updateOutOfOfficeStatus,
  RichTextEditorProps,
  scheduledDates,
  outOfOfficeEnabled,
}) => {
  const {
    customConfig: rtePropsCustomConfig,
    ...restRichTextEditorProps
  } = RichTextEditorProps;

  const hasOutOfOfficeMessage = Boolean(outOfOfficeMessage.length);
  const dateTimeChecksOut = moment(scheduledDates.start).isBefore(scheduledDates.end, 'day') || moment(scheduledDates.start).isSame(scheduledDates.end, 'day');
  const canSubmitOutOfOffice = scheduledDates.start?._isValid
    && scheduledDates.end?._isValid
    && hasOutOfOfficeMessage
    && dateTimeChecksOut;

  const isMobile = useMobileLayout();
  const { templates } = useTemplatesStateContext();

  const onInsertTemplate = (template) => {
    setOutOfOfficeMessage(template.attr('content'));
  };

  return (
    <div
      className={classNames(classes.root, className)}
    >
      <Typography
        className={classes.title}
        color="primary"
        align="left"
        variant="h2"
      >
        {'Away Message'}
      </Typography>
      {!outOfOfficeEnabled && (
        <RichTextEditor
          className={classes.richTextEditor}
          value={outOfOfficeMessage}
          onChange={setOutOfOfficeMessage}
          customConfig={{
            extraPlugins: 'autogrow,editorplaceholder',
            toolbarLocation: 'bottom',
            toolbarGroups: [
              { name: 'document', groups: ['mode', 'document', 'doctools'] },
              { name: 'forms' },
              { name: 'basicstyles', groups: ['basicstyles', 'cleanup'] },
              {
                name: 'paragraph',
                groups: ['list', 'bidi'],
              },
              { name: 'links' },
              { name: 'styles' },
              { name: 'tools' },
            ],
            removePlugins:
              'elementspath,scayt,contextmenu,liststyle,tabletools,tableselection,stylescombo,format,font,justify',
            autoGrow_onStartup: true,
            editorplaceholder: 'Write your custom away message here.',
            height: 50,
            autoGrow_minHeight: 120,
            autoGrow_maxHeight: 320,
            ...rtePropsCustomConfig,
          }}
          test={'new-out-of-office-message-input'}
          {...restRichTextEditorProps}
        />)
      }
      {outOfOfficeEnabled && (
        <div className={classes.richTextDisplay}>
          <RichTextDisplay
            content={outOfOfficeMessage}
          />
        </div>
      )}
      {!outOfOfficeEnabled && (
        <div
          className={classNames(classes.actions, {
            [classes.actionsMobile]: isMobile,
          })}
        >
          <TemplateOptionsMenu
            useLegacyButton
            className={classes.mcTemplateOptionsMenu}
            templates={templates}
            onInsertTemplate={template => {
              onInsertTemplate(template);
            }}
            templateDraftContent={outOfOfficeMessage}
            templateType={TEMPLATE_TYPES.outOfOffice}
            confirmBeforeInsert
          />
          <Button
            variant={'contained'}
            color={'secondary'}
            onClick={updateOutOfOfficeStatus}
            disabled={!canSubmitOutOfOffice}
            disableElevation
            fullWidth={isMobile}
            test={'enable-out-of-office-button'}
          >
            {'Enable Out of Office Time'}
          </Button>
        </div>)
      }
    </div>
  );
};

OutOfOfficeMessageEditor.defaultProps = {
  className: '',
  RichTextEditorProps: {},
  outOfOfficeMessage: '',
};

OutOfOfficeMessageEditor.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  outOfOfficeMessage: PropTypes.string,
  RichTextEditorProps: PropTypes.object,
  setOutOfOfficeMessage: PropTypes.func.isRequired,
  updateOutOfOfficeStatus: PropTypes.func.isRequired,
  outOfOfficeEnabled: PropTypes.bool.isRequired,
};

export default withStyles(styles)(OutOfOfficeMessageEditor);
